import { useMemo } from 'react'

import { useAddresses } from '@/api/addresses/queries/useAddresses'
import AddIcon from '@/components/icons/add.svg?react'
import CheckmarkIcon from '@/components/icons/checkmark.svg?react'
import PkgUniteIcon from '@/components/icons/pkg-unite.svg?react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'

interface Props {
  selectedAddressId: string
  onSelectAddress: (addressId: string) => void
  onAddAddress: () => void
}

export const AddressSelector = ({ selectedAddressId, onSelectAddress, onAddAddress }: Props) => {
  const { data: addresses = [], isLoading } = useAddresses()

  // Sort addresses: selectedfirst, default first, then by creation date
  const sortedAddresses = useMemo(
    () =>
      addresses.sort((a, b) => {
        if (a.id === selectedAddressId) return -1
        if (b.id === selectedAddressId) return 1
        if (a.isDefault) return -1
        if (b.isDefault) return 1
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      }),
    [addresses, selectedAddressId]
  )

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div className='space-y-3'>
      {sortedAddresses.length === 0 ? (
        <div className='text-text-secondary'>
          אין כתובות.{' '}
          <button onClick={onAddAddress} className='text-text-link cursor-pointer'>
            הוסף כתובת חדשה כדי להתחיל.
          </button>
        </div>
      ) : (
        sortedAddresses.map(address => (
          <Card key={address.id} className='p-4'>
            <CardContent className='flex gap-4 p-0'>
              {selectedAddressId === address.id && <CheckmarkIcon className='text-icon-primary size-4' />}
              <div className='flex grow flex-col'>
                <div className='text-text-secondary text-sm'>
                  {address.street}
                  {address.apartment && `, דירה ${address.apartment}`}
                  <br />
                  {address.city}, {address.postalCode}
                </div>
                {address.note && <div className='text-text-secondary text-xs'>{address.note}</div>}
              </div>
              {selectedAddressId !== address.id && (
                <Button
                  variant='secondary'
                  className='self-end px-4'
                  icon={<PkgUniteIcon className='size-6' />}
                  onClick={() => onSelectAddress(address.id)}
                >
                  שלח לכתובת זו
                </Button>
              )}
            </CardContent>
          </Card>
        ))
      )}

      {/* Add New Address */}
      {sortedAddresses.length > 0 && (
        <div className=''>
          <Button
            icon={<AddIcon className='size-3' />}
            variant='ghost'
            size='sm'
            onClick={onAddAddress}
            className='text-text-link gap-2 text-xs hover:underline'
          >
            הוסף כתובת
          </Button>
        </div>
      )}
    </div>
  )
}
